/**
 * Single chat message bubble with avatar.
 *
 * - User messages: right-aligned with user avatar, white bordered chip.
 * - Assistant messages: left-aligned with Hobbes avatar, subtle bg.
 * Directional slide-in animations matching the Interviewer UX.
 */

import React from 'react';
import { motion } from 'framer-motion';
import ReactMarkdown from 'react-markdown';
import { User } from 'lucide-react';
import { HobbesAvatar } from '@/components/interview/HobbesAvatar';
import { messageVariantsLeft, messageVariantsRight, easeOut } from './animations';

interface Props {
  role: 'user' | 'assistant';
  content: string;
}

const ChatMessage: React.FC<Props> = ({ role, content }) => {
  const isUser = role === 'user';

  return (
    <motion.div
      variants={isUser ? messageVariantsRight : messageVariantsLeft}
      initial="initial"
      animate="animate"
      transition={easeOut}
      className={`flex ${isUser ? 'justify-end' : 'justify-start'}`}
    >
      <div className={`flex gap-3 max-w-[80%] ${isUser ? 'flex-row-reverse' : ''}`}>
        {/* Avatar */}
        {isUser ? (
          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-secondary text-muted-foreground">
            <User className="h-4 w-4" />
          </div>
        ) : (
          <HobbesAvatar size="sm" />
        )}

        {/* Bubble */}
        <div
          className={`rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${
            isUser
              ? 'rounded-tr-md border border-border/60 bg-card text-heading'
              : 'rounded-tl-md bg-muted/50 text-body'
          }`}
        >
          {isUser ? (
            <p className="whitespace-pre-wrap">{content}</p>
          ) : (
            <div className="prose prose-sm dark:prose-invert max-w-none [&>p]:mb-2 [&>p:last-child]:mb-0">
              <ReactMarkdown>{content}</ReactMarkdown>
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
};

export default ChatMessage;
